import "./ProyectosCommon.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { MacroProyectoResponse } from "../../../shared/response/proyectos/MacroProyectoResponse";
import { CosechaResponse } from "../../../shared/response/proyectos/CosechaResponse";
import { CultivoResponse } from "../../../shared/response/proyectos/CultivoResponse";
import { MacroProyectoPayload } from "../../../shared/services/api/payload/proyectos/MacroProyectoPayload";
import { MacroProyectoSaveService } from "../../services/proyectos/MacroProyectoSaveService";

export class MacroProyectoFormModal extends BaseSmallModalFormComponent<
  MacroProyectoResponse,
  MacroProyectoPayload,
  MacroProyectoSaveService
> {
  private _nombre!: HTMLInputElement;
  private _desc!:   HTMLTextAreaElement;

  constructor(
    private readonly _cosechas: CosechaResponse[],
    private readonly _cultivos: CultivoResponse[],
    private readonly _onSavedCb: (item: MacroProyectoResponse) => void,
    private readonly _item?: MacroProyectoResponse,
  ) {
    super(new MacroProyectoSaveService());
  }

  /**
   * Cosecha del Macro-Proyecto. **Ya no se elige a mano** (2026-10-30): editando es la que tiene -el select
   * estaba deshabilitado de todas formas, la cosecha no se cambia después-, y creando es la **vigente**,
   * que es por definición la que fija el precio del catálogo ahora mismo. Elegir otra solo servía para
   * equivocarse: un Macro-Proyecto nuevo con las tasas IPC de una cosecha vieja calcula plata mal.
   *
   * Sin cosecha vigente cae a la activa más reciente, y si no hay ninguna devuelve null: el servidor la
   * exige, así que el modal lo dice y no deja guardar en vez de fallar al enviar.
   */
  private get _cosecha(): CosechaResponse | null {
    if (this._item) return this._cosechas.find((c) => c.id === this._item!.cosechaId) ?? null;
    return this._cosechas.find((c) => c.vigente)
        ?? [...this._cosechas].filter((c) => c.estado === "activa").sort((a, b) => b.anio - a.anio)[0]
        ?? null;
  }

  protected get title(): string { return this._item ? "Editar Macro-Proyecto" : "Nuevo Macro-Proyecto"; }
  protected get saveText(): string { return "Guardar"; }

  protected renderBody(): string {
    const m = this._item;
    const cultivoIds = new Set(m?.cultivoIds ?? []);
    return `
      <div class="cl-form-fields">
        <div class="cl-form-group">
          <label class="cl-form-label" for="mp-nombre">Nombre <span class="cl-required">*</span></label>
          <input id="mp-nombre" class="sx-input" type="text" maxlength="200" value="${this._esc(m?.nombre ?? "")}" placeholder="Ej: Cosecha 2026 · Santander Sur">
        </div>
        ${this._cosechaNotaHtml()}
        <div class="cl-form-group">
          <label class="cl-form-label">Cultivos incluidos</label>
          <div style="display:flex;flex-wrap:wrap;gap:10px">
            ${this._cultivos.map((c) => `
              <label class="pr-check">
                <input type="checkbox" data-cultivo="${c.id}" ${cultivoIds.has(c.id) ? "checked" : ""}>
                <span>${this._esc(c.nombre)}</span>
              </label>`).join("")}
          </div>
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="mp-desc">Descripción (opcional)</label>
          <textarea id="mp-desc" class="sx-input" rows="2" style="resize:vertical" placeholder="Zona de influencia, entidad financiera, notas…">${this._esc(m?.descripcion ?? "")}</textarea>
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._nombre = el.querySelector<HTMLInputElement>("#mp-nombre")!;
    this._desc   = el.querySelector<HTMLTextAreaElement>("#mp-desc")!;

    this._nombre.addEventListener("input", () => this._validate());
    this._validate();
  }

  protected buildPayload(): MacroProyectoPayload {
    const cultivoIds = Array.from(
      this.getElement().querySelectorAll<HTMLInputElement>("input[data-cultivo]:checked"),
    ).map((cb) => cb.dataset.cultivo!);

    return {
      id: this._item?.id ?? null,
      nombre: this._nombre.value.trim(),
      cosechaId: this._cosecha?.id ?? "",
      // La región ya no se pide. Editando se reenvía la que tenía, para no borrarle el dato a los
      // Macro-Proyectos que ya la traen; creando va vacia.
      region: this._item?.region ?? "",
      descripcion: this._desc.value.trim() || null,
      cultivoIds,
    };
  }

  protected override onSaved(item: MacroProyectoResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  private _validate(): void {
    this.setSaveDisabled(this._nombre.value.trim().length < 1 || this._cosecha === null);
  }

  /**
   * Dice con qué cosecha va a calcular, sin pedirla. Es plata: de ahí salen las tasas IPC de todos los
   * proyectos del lote, así que verla no sobra aunque ya no se elija.
   */
  private _cosechaNotaHtml(): string {
    const c = this._cosecha;
    if (c === null) {
      return `<div class="pr-alert-warn" style="margin:0 0 4px">
                No hay ninguna cosecha vigente. Crea una en <strong>Parámetros</strong> antes de crear el Macro-Proyecto:
                de ella salen las tasas IPC con las que se calculan los presupuestos.
              </div>`;
    }
    return `<div class="pr-form-note" style="margin:0 0 4px">
              Temporada: <strong>${this._esc(c.nombre)}</strong> · IPC base ${(c.tasaBaseIpc * 100).toFixed(1)}%
              ${c.frozen ? " · congelada" : ""}. ${this._item ? "No se cambia después de creado." : "Es la cosecha vigente."}
            </div>`;
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
